import React, { useEffect, useState } from "react";
import { FONT_BODY } from "../../utils/designTokens";
import {
  perfDebugEnabled,
  subscribePerf,
  getPerfState,
  readNavigation,
  readPaint,
  readAssets,
  readImages,
  readDevice,
  analyzeWaterfall,
  buildReport,
  listOps,
  splitOp,
} from "../../utils/perfDebug";

// Visible timing readout, shown only when the URL carried debug=1. Sized and
// coloured to survive being screenshotted on a phone: big summary line, high
// contrast, monospace columns.

const ms = (n) => (n == null ? "—" : `${Math.round(n)}ms`);
const secs = (n) => (n == null ? "—" : `${(n / 1000).toFixed(2)}s`);
const kb = (n) => (n == null ? "—" : `${(n / 1024).toFixed(0)}KB`);

// Green under half a second, amber to a second and a half, red past that.
const heat = (n) => (n < 500 ? "#4ade80" : n < 1500 ? "#fbbf24" : "#f87171");

const Row = ({ label, value, color }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
    <span style={{ color: "#94a3b8", whiteSpace: "nowrap" }}>{label}</span>
    <span style={{ color: color || "#e2e8f0", fontWeight: 700, whiteSpace: "nowrap" }}>{value}</span>
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginTop: 8 }}>
    <div style={{ color: "#38bdf8", fontWeight: 800, letterSpacing: "0.08em", fontSize: 10, marginBottom: 3 }}>
      {title}
    </div>
    {children}
  </div>
);

// A finding that needs no interpretation — a public database proxy, an
// uncompressed megabyte. Loud on purpose; these are the one-line fixes.
const Flag = ({ children }) => (
  <div
    style={{
      background: "rgba(248,113,113,0.14)",
      border: "1px solid rgba(248,113,113,0.5)",
      borderRadius: 4,
      padding: "3px 6px",
      margin: "3px 0",
      color: "#fca5a5",
      fontWeight: 700,
    }}
  >
    ⚠ {children}
  </div>
);

// A phase of server work drawn as a proportional bar, indented to its nesting
// depth so "zoneFilter → zoneFilter:platformFee ×N" reads as a tree.
const PhaseRow = ({ phase, totalMs }) => {
  const pct = totalMs > 0 ? (phase.ms / totalMs) * 100 : 0;
  return (
    <div style={{ marginBottom: 2, paddingLeft: phase.depth * 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <span style={{ color: "#cbd5e1", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {phase.name}
          {phase.calls > 1 && (
            <span style={{ color: "#f472b6", fontWeight: 800 }}>
              {" "}×{phase.calls}
            </span>
          )}
        </span>
        <span style={{ color: heat(phase.ms), fontWeight: 700, whiteSpace: "nowrap" }}>
          {ms(phase.ms)} · {Math.round(pct)}%{phase.queries ? ` · ${phase.queries}q` : ""}
          {phase.calls > 1 ? ` · ${ms(phase.msPerCall)} ea` : ""}
        </span>
      </div>
      <div style={{ height: 3, background: "rgba(148,163,184,0.15)", borderRadius: 2, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${Math.max(1, Math.min(100, pct))}%`, background: heat(phase.ms) }} />
      </div>
    </div>
  );
};

/**
 * Everything about the connection to the database. This block is the reason
 * the probe was reopened: if a bare `SELECT 1` on a warm pool costs about what
 * every real statement costs, then no amount of query tuning will help and the
 * fix is the endpoint, not the SQL.
 */
const DatabaseBlock = ({ db, sqlCount, totalMs }) => {
  if (!db) return null;
  const proxied = /PROXY/i.test(db.endpoint);
  const tollPct = db.tollMs && totalMs ? Math.round((db.tollMs / totalMs) * 100) : null;
  return (
    <>
      <div style={{ color: "#38bdf8", fontWeight: 800, fontSize: 10, margin: "6px 0 3px" }}>DATABASE</div>
      {proxied && <Flag>DB_HOST is the public proxy — every one of these {sqlCount} statements crosses the internet. Switch it to the private host.</Flag>}
      <Row label="endpoint" value={`${db.endpoint} :${db.port}`} color={proxied ? "#f87171" : "#4ade80"} />
      {(db.pings || []).map((p, i) => (
        <Row
          key={i}
          label={`bare SELECT 1 · ${p.label}`}
          value={p.ms == null ? p.error : ms(p.ms)}
          color={p.ms == null ? "#f87171" : heat(p.ms * 10)}
        />
      ))}
      {db.floorMs != null && (
        <Row
          label={`floor × ${sqlCount} statements`}
          value={`${ms(db.floorMs)} each = ${ms(db.tollMs)}${tollPct != null ? ` · ${tollPct}% of the request` : ""}`}
          color={tollPct > 40 ? "#f87171" : "#e2e8f0"}
        />
      )}
      <Row
        label="new connections opened"
        value={`${db.connectionsOpened}${db.connectionsOpened ? ` · ${ms(db.connectMs)} handshaking` : ""}`}
        color={db.connectionsOpened > 0 ? "#fbbf24" : "#4ade80"}
      />
      {db.connectionsOpened > 0 && (
        <Flag>pool opened {db.connectionsOpened} fresh connection(s) mid-request — pool.min is 0, so a quiet site never keeps one warm.</Flag>
      )}
      {db.poolBefore && (
        <Row
          label="pool at start"
          value={`${db.poolBefore.size}/${db.poolBefore.max} open · ${db.poolBefore.available} idle · ${db.poolBefore.using} busy · ${db.poolBefore.waiting} waiting`}
          color={db.poolBefore.waiting > 0 ? "#f87171" : undefined}
        />
      )}
      {db.poolAfter && (
        <Row
          label="pool at end"
          value={`${db.poolAfter.size}/${db.poolAfter.max} open · ${db.poolAfter.available} idle · ${db.poolAfter.using} busy · ${db.poolAfter.waiting} waiting`}
        />
      )}
    </>
  );
};

/**
 * Server time split into "waiting on the database" and "our own code", from
 * merged statement intervals — so it stays correct even when Sequelize fires
 * its `separate: true` includes concurrently and the naive subtraction lies.
 */
const TimelineBlock = ({ timeline, gql, totalMs }) => {
  if (!timeline) return null;
  const dbPct = totalMs ? Math.round((timeline.dbBusyMs / totalMs) * 100) : 0;
  return (
    <>
      <div style={{ color: "#38bdf8", fontWeight: 800, fontSize: 10, margin: "6px 0 3px" }}>WHERE THE SERVER TIME WENT</div>
      <Row label="waiting on database" value={`${ms(timeline.dbBusyMs)} · ${dbPct}%`} color={heat(timeline.dbBusyMs)} />
      <Row label="everything else (our JS)" value={ms(timeline.nonDbMs)} color={heat(timeline.nonDbMs)} />
      {gql && (
        <Row
          label="apollo parse/validate/exec"
          value={`${ms(gql.parseMs)} / ${ms(gql.validateMs)} / ${ms(gql.executeMs)}`}
        />
      )}
      {gql?.responseBytes != null && (
        <Row label="serialize response" value={`${ms(gql.serializeMs)} for ${kb(gql.responseBytes)}`} color={heat(gql.serializeMs)} />
      )}
      {(timeline.gaps || []).slice(0, 3).map((g, i) => (
        <Row
          key={i}
          label={`  dead air at ${ms(g.at)} · ${g.after} → ${g.before}`}
          value={ms(g.ms)}
          color={heat(g.ms)}
        />
      ))}
    </>
  );
};

// The browser's own view of one request: how long it sat queued, the handshake,
// the server thinking, and the body arriving. Separates a slow server from a
// fat payload, which the server trace alone cannot do.
const NetworkBlock = ({ net }) => {
  if (!net) return null;
  if (net.blocked) {
    return (
      <Row label="connection breakdown" value="unavailable — deploy the Timing-Allow-Origin header" color="#fbbf24" />
    );
  }
  return (
    <>
      <Row
        label="  stall / dns / tcp / tls"
        value={`${ms(net.stallMs)} · ${ms(net.dnsMs)} · ${ms(net.tcpMs)} · ${ms(net.tlsMs)}`}
        color={net.stallMs > 200 ? "#fbbf24" : undefined}
      />
      <Row
        label="  ttfb / download"
        value={`${ms(net.ttfbMs)} · ${ms(net.downloadMs)}`}
        color={heat(Math.max(net.ttfbMs, net.downloadMs))}
      />
      <Row
        label="  bytes on the wire / json"
        value={`${kb(net.wireBytes)} / ${kb(net.jsonBytes)}${net.compressed ? " (gzip)" : ""}`}
        color={!net.compressed && net.jsonBytes > 51200 ? "#f87171" : undefined}
      />
      {!net.compressed && net.jsonBytes > 51200 && (
        <Flag>{kb(net.jsonBytes)} sent uncompressed — enabling gzip/brotli on the API cuts this to roughly a tenth.</Flag>
      )}
    </>
  );
};

/**
 * The projection that answers "what happens when more restaurants sit in this
 * area". Everything that ran once per restaurant is linear; everything else is
 * treated as fixed. Deliberately crude — it is a shape, not a promise — but it
 * turns a 2-restaurant reading into an argument about 10 or 50.
 */
const Projection = ({ server }) => {
  const n = server.notes?.restaurantsFound || 0;
  if (!n) return null;

  // Anything that closed more than once is per-restaurant work. Counting calls
  // rather than matching names matters because the zone loop splits its
  // iterations across the platform and self-delivery paths — neither branch on
  // its own reaches the restaurant count, but together they are the loop.
  const perRestaurant = (server.phases || [])
    .filter((p) => p.calls > 1)
    .reduce((a, p) => a + p.ms, 0);
  // Formatting and the menu payload also grow with restaurants even though they
  // run as a single call, so scale them by measured cost per restaurant.
  const linearNamed = (server.phases || [])
    .filter((p) => p.calls <= 1 && /formatRestaurant|restaurants\+menus|serviceLocations/.test(p.name))
    .reduce((a, p) => a + p.ms, 0);
  const linear = (perRestaurant + linearNamed) / n;
  const fixed = Math.max(0, server.totalMs - perRestaurant - linearNamed);

  return (
    <div style={{ marginTop: 4, paddingTop: 4, borderTop: "1px dashed rgba(148,163,184,0.25)" }}>
      <Row label={`fixed cost (runs once)`} value={ms(fixed)} />
      <Row label={`per-restaurant cost`} value={`${ms(linear)} × ${n} today`} color="#f472b6" />
      {[10, 25, 50].map((count) => (
        <Row
          key={count}
          label={`projected server time @ ${count} restaurants`}
          value={ms(fixed + linear * count)}
          color={heat(fixed + linear * count)}
        />
      ))}
    </div>
  );
};

export default function PerfDebugPanel() {
  const [, setTick] = useState(0);
  const [open, setOpen] = useState(true);
  const [copied, setCopied] = useState(false);
  // Which operation's server breakdown is expanded. Only one at a time — the
  // panel has to stay screenshot-sized.
  const [openOp, setOpenOp] = useState(null);

  useEffect(() => {
    if (!perfDebugEnabled) return undefined;
    const rerender = () => setTick((t) => t + 1);
    const unsubscribe = subscribePerf(rerender);
    // Navigation/resource entries land after mount (load event, late chunks),
    // so poll briefly rather than miss them.
    const timer = setInterval(rerender, 1000);
    const stop = setTimeout(() => clearInterval(timer), 30000);
    return () => {
      unsubscribe();
      clearInterval(timer);
      clearTimeout(stop);
    };
  }, []);

  if (!perfDebugEnabled) return null;

  const { marks, longTasks, spans, counters } = getPerfState();
  const ops = listOps();
  const nav = readNavigation();
  const paint = readPaint();
  const assets = readAssets();
  const images = readImages();
  const dev = readDevice();
  const flow = analyzeWaterfall();
  const assetBytes = assets.reduce((a, r) => a + r.bytes, 0);
  const slowest = ops.slice().sort((a, b) => b.ms - a.ms)[0];
  const slowestSplit = slowest ? splitOp(slowest) : { serverMs: null, networkMs: null };
  const networkTotal = ops.reduce((a, o) => a + o.ms, 0);
  const blockedMs = longTasks.reduce((a, t) => a + t.ms, 0);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildReport());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setCopied(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2147483000,
        background: "rgba(6,11,20,0.96)",
        color: "#e2e8f0",
        borderTop: "2px solid #38bdf8",
        fontFamily: FONT_BODY,
        fontVariantNumeric: "tabular-nums",
        fontSize: 11,
        lineHeight: 1.45,
        maxHeight: open ? "52vh" : "auto",
        overflowY: "auto",
        boxShadow: "0 -6px 24px rgba(0,0,0,0.45)",
      }}
    >
      {/* Summary bar — the one line that must be legible in any screenshot */}
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 12px",
          background: "rgba(56,189,248,0.10)",
          cursor: "pointer",
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 800, color: "#38bdf8" }}>PERF</span>
        <span style={{ fontSize: 13, fontWeight: 800 }}>
          paint <span style={{ color: paint.fcp ? heat(paint.fcp) : "#e2e8f0" }}>{secs(paint.fcp)}</span>
        </span>
        <span style={{ fontSize: 13, fontWeight: 800 }}>
          loaded <span style={{ color: paint.lcp ? heat(paint.lcp) : "#e2e8f0" }}>{secs(paint.lcp)}</span>
        </span>
        <span style={{ fontSize: 13, fontWeight: 800 }}>
          slowest{" "}
          <span style={{ color: slowest ? heat(slowest.ms) : "#e2e8f0" }}>
            {slowest ? `${slowest.name} ${secs(slowest.ms)}` : "—"}
          </span>
        </span>
        {slowestSplit.serverMs != null && (
          <span style={{ fontSize: 13, fontWeight: 800 }}>
            server{" "}
            <span style={{ color: heat(slowestSplit.serverMs) }}>{secs(slowestSplit.serverMs)}</span>
            <span style={{ color: "#94a3b8", fontWeight: 600 }}> / net </span>
            <span style={{ color: heat(slowestSplit.networkMs) }}>{secs(slowestSplit.networkMs)}</span>
          </span>
        )}
        <span style={{ fontSize: 13, fontWeight: 800 }}>
          blocked <span style={{ color: heat(blockedMs) }}>{secs(blockedMs)}</span>
        </span>
        <span style={{ fontSize: 13, fontWeight: 800 }}>js {kb(assetBytes)}</span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCopy();
            }}
            style={{
              background: "#38bdf8",
              color: "#04121f",
              border: 0,
              borderRadius: 6,
              padding: "4px 10px",
              fontWeight: 800,
              fontSize: 11,
              fontFamily: "inherit",
              cursor: "pointer",
            }}
          >
            {copied ? "Copied" : "Copy"}
          </button>
          <span style={{ color: "#94a3b8" }}>{open ? "▼ hide" : "▲ show"}</span>
        </span>
      </div>

      {open && (
        <div style={{ padding: "6px 12px 14px" }}>
          <Section title="DEVICE">
            <Row
              label="cores / memory / net"
              value={`${dev.cores || "?"} / ${dev.memory ? `${dev.memory}GB` : "?"} / ${dev.net || "?"}${
                dev.downlink ? ` ${dev.downlink}Mbps` : ""
              }${dev.rtt ? ` rtt ${dev.rtt}ms` : ""}`}
            />
            <Row label="js heap in use" value={dev.heapMb ? `${dev.heapMb}MB` : "—"} />
            <Row label="inside iframe" value={dev.embedded ? "yes (embed)" : "no"} />
          </Section>

          {nav && (
            <Section title="DOCUMENT">
              <Row label="html ttfb" value={ms(nav.ttfb)} color={heat(nav.ttfb)} />
              <Row label="dom interactive" value={ms(nav.domInteractive)} color={heat(nav.domInteractive)} />
              <Row label="dom content loaded" value={ms(nav.domContentLoaded)} color={heat(nav.domContentLoaded)} />
              <Row label="load event" value={ms(nav.load)} color={nav.load ? heat(nav.load) : undefined} />
              <Row label="first paint" value={ms(paint.fcp)} color={paint.fcp ? heat(paint.fcp) : undefined} />
              <Row label="largest paint (felt load)" value={ms(paint.lcp)} color={paint.lcp ? heat(paint.lcp) : undefined} />
            </Section>
          )}

          <Section title={`ASSETS · ${kb(assetBytes)} js/css · ${kb(images.bytes)} in ${images.count} images`}>
            {assets.slice(0, 5).map((a) => (
              <Row
                key={a.name}
                label={`${a.name.slice(0, 28)}${a.cached ? " (cached)" : ""}`}
                value={`${kb(a.bytes)} · ${ms(a.ms)}`}
                color={heat(a.ms)}
              />
            ))}
            {images.slowest.map((i) => (
              <Row key={i.name} label={`img ${i.name}`} value={ms(i.ms)} color={heat(i.ms)} />
            ))}
          </Section>

          {/* Was the network the bottleneck, or was it idle while the app
              waited on itself? The largest gap is the answer. */}
          {flow && (
            <Section title="REQUEST WATERFALL">
              <Row label="first request fired" value={ms(flow.firstStart)} color={heat(flow.firstStart)} />
              <Row label="last response landed" value={ms(flow.lastEnd)} color={heat(flow.lastEnd)} />
              <Row
                label="network busy / idle"
                value={`${ms(flow.busyMs)} busy · ${ms(flow.idleMs)} idle`}
                color={flow.idleMs > 500 ? "#fbbf24" : undefined}
              />
              <Row
                label="sum of durations"
                value={`${ms(flow.serialMs)} (well over busy = they overlapped)`}
              />
              {flow.largestGap && (
                <Row
                  label={`largest dead gap · ${flow.largestGap.after} → ${flow.largestGap.before}`}
                  value={`${ms(flow.largestGap.ms)} at ${ms(flow.largestGap.at)}`}
                  color={heat(flow.largestGap.ms)}
                />
              )}
            </Section>
          )}

          {longTasks.length > 0 && (
            <Section title={`MAIN THREAD BLOCKED · ${longTasks.length} tasks · ${ms(blockedMs)} total`}>
              {longTasks
                .slice()
                .sort((a, b) => b.ms - a.ms)
                .slice(0, 5)
                .map((t, i) => (
                  <Row key={i} label={`block at ${ms(t.at)}${t.source ? ` · ${t.source}` : ""}`} value={ms(t.ms)} color={heat(t.ms * 3)} />
                ))}
            </Section>
          )}

          {(spans.length > 0 || Object.keys(counters).length > 0) && (
            <Section title="CLIENT WORK">
              {spans
                .slice()
                .sort((a, b) => b.ms - a.ms)
                .map((s) => (
                  <Row
                    key={s.name}
                    label={`${s.name}${s.calls > 1 ? ` ×${s.calls}` : ""}`}
                    value={ms(s.ms)}
                    color={heat(s.ms * 3)}
                  />
                ))}
              {Object.entries(counters).map(([k, v]) => (
                <Row key={k} label={k} value={String(v)} />
              ))}
            </Section>
          )}

          {marks.length > 0 && (
            <Section title="APP MILESTONES · since navigation start">
              {marks
                .slice()
                .sort((a, b) => a.at - b.at)
                .map((m) => (
                  <Row key={m.name} label={m.name} value={ms(m.at)} color={heat(m.at)} />
                ))}
            </Section>
          )}

          <Section title={`GRAPHQL · ${ops.length} network calls · ${ms(networkTotal)} total`}>
            {ops.length === 0 && <div style={{ color: "#94a3b8" }}>no network queries yet…</div>}
            {/* Client timings work on their own; the phase breakdown needs the
                backend half of the probe to be live. Say so rather than leaving
                the rows mysteriously unexpandable. */}
            {ops.length > 0 && !ops.some((o) => o.server) && (
              <div style={{ color: "#fbbf24", marginBottom: 4 }}>
                no server breakdown — the backend this app talks to has not been deployed with
                the perf trace yet. Client timings below are still accurate.
              </div>
            )}
            {ops.some((o) => o.server) && (
              <div style={{ color: "#94a3b8", marginBottom: 4 }}>tap a query to open its server breakdown</div>
            )}
            {ops.map((o, i) => {
              const { serverMs, networkMs } = splitOp(o);
              const expanded = openOp === `${o.name}-${i}`;
              return (
                <div key={`${o.name}-${i}`} style={{ marginBottom: 6 }}>
                  <div
                    onClick={() => setOpenOp(expanded ? null : `${o.name}-${i}`)}
                    style={{ cursor: o.server ? "pointer" : "default" }}
                  >
                    <Row
                      label={`${o.server ? (expanded ? "▾ " : "▸ ") : "  "}${o.name}${o.error ? " [ERROR]" : ""}`}
                      value={`${ms(o.ms)} · ${kb(o.bytes)} · begins ${ms(o.startedAt)}`}
                      color={heat(o.ms)}
                    />
                  </div>
                  {/* Bar starts where the call started, so serialised waterfalls are obvious */}
                  <div style={{ height: 4, background: "rgba(148,163,184,0.18)", borderRadius: 2, overflow: "hidden" }}>
                    <div
                      style={{
                        height: "100%",
                        marginLeft: `${Math.min(95, (o.startedAt / 6000) * 100)}%`,
                        width: `${Math.max(1, Math.min(100, (o.ms / 6000) * 100))}%`,
                        background: heat(o.ms),
                      }}
                    />
                  </div>

                  {/* The split that decides where to look: server-side work vs
                      everything the browser paid for on top of it. */}
                  {serverMs != null && (
                    <div style={{ paddingLeft: 12, marginTop: 2 }}>
                      <Row
                        label="server busy"
                        value={`${ms(serverMs)} (sql ${o.server.sqlCount}q/${ms(o.server.sqlMs)} · ${
                          o.server.sqlOverlapped ? "queries overlapped" : `js ${ms(o.server.cpuMs)}`
                        })`}
                        color={heat(serverMs)}
                      />
                      <Row label="network + parse" value={ms(networkMs)} color={heat(networkMs)} />
                    </div>
                  )}

                  {/* The browser's own connection breakdown, always shown: it
                      is what tells a slow server from a fat payload. */}
                  <div style={{ paddingLeft: 12 }}>
                    <NetworkBlock net={o.net} />
                  </div>

                  {expanded && o.server && (
                    <div
                      style={{
                        marginTop: 4,
                        marginLeft: 12,
                        padding: "6px 8px",
                        background: "rgba(56,189,248,0.06)",
                        borderLeft: "2px solid rgba(56,189,248,0.5)",
                        borderRadius: 4,
                      }}
                    >
                      <DatabaseBlock db={o.server.db} sqlCount={o.server.sqlCount} totalMs={o.server.totalMs} />
                      <TimelineBlock timeline={o.server.timeline} gql={o.server.gql} totalMs={o.server.totalMs} />

                      <div style={{ color: "#38bdf8", fontWeight: 800, fontSize: 10, margin: "6px 0 3px" }}>
                        SERVER PHASES
                      </div>
                      {(o.server.phases || []).length === 0 && (
                        <div style={{ color: "#94a3b8" }}>
                          no phases recorded — this resolver is not instrumented yet
                        </div>
                      )}
                      {(o.server.phases || []).map((p, pi) => (
                        <PhaseRow key={`${p.name}-${pi}`} phase={p} totalMs={o.server.totalMs} />
                      ))}

                      {(o.server.fieldHotspots || []).length > 0 && (
                        <>
                          <div style={{ color: "#38bdf8", fontWeight: 800, fontSize: 10, margin: "6px 0 3px" }}>
                            SLOWEST RESOLVERS
                          </div>
                          {o.server.fieldHotspots.map((f) => (
                            <Row
                              key={f.name}
                              label={`${f.name}${f.calls > 1 ? ` ×${f.calls}` : ""}${f.queries ? ` · ${f.queries}q` : ""}`}
                              value={`${ms(f.ms)}${f.calls > 1 ? ` · ${ms(f.msPerCall)} ea` : ""}`}
                              color={f.calls > 5 ? "#f472b6" : heat(f.ms)}
                            />
                          ))}
                        </>
                      )}

                      {(o.server.queries || []).length > 0 && (
                        <>
                          <div style={{ color: "#38bdf8", fontWeight: 800, fontSize: 10, margin: "6px 0 3px" }}>
                            SQL BY SHAPE {o.server.sqlExposed ? "" : "(statements hidden — set PERF_TRACE_SQL=1)"}
                          </div>
                          {o.server.queries.map((q, qi) => (
                            <div key={qi}>
                              <Row
                                label={`${q.shape}${q.count > 1 ? ` ×${q.count}` : ""}${q.phase ? ` · in ${q.phase}` : ""}`}
                                value={`${ms(q.ms)}${q.count > 1 && q.maxMs != null ? ` · worst ${ms(q.maxMs)}` : ""}`}
                                color={q.count > 5 ? "#f472b6" : heat(q.ms)}
                              />
                              {q.sample && (
                                <div style={{ color: "#64748b", fontSize: 10, wordBreak: "break-all", marginBottom: 2 }}>
                                  {q.sample}
                                </div>
                              )}
                            </div>
                          ))}
                        </>
                      )}

                      {Object.keys(o.server.notes || {}).length > 0 && (
                        <>
                          <div style={{ color: "#38bdf8", fontWeight: 800, fontSize: 10, margin: "6px 0 3px" }}>
                            WHAT WAS LOADED
                          </div>
                          {Object.entries(o.server.notes).map(([k, v]) => (
                            <Row key={k} label={k} value={String(v)} color={/Discarded/.test(k) && v > 0 ? "#fbbf24" : undefined} />
                          ))}
                          <Projection server={o.server} />
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </Section>
        </div>
      )}
    </div>
  );
}
